import _ from 'lodash';
import {DateTime} from 'luxon';
import React, {FC, useMemo} from 'react';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {RendererOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {SmartDateTimeFormatterContainer} from '../../../../srcCommon/formatters/dateTime/smartDateTimeFormatterContainer';
import {compareName} from '../../../../srcCommon/helpers/localeHelpers';
import {buildCustomFieldLabel} from '../../../../srcCommon/helpers/models/customFieldHelpers';
import {getValidTimestampFromValue} from '../../../helpers/dateTimeHelpers';
import {buildTeammateDisplayName} from '../../../helpers/modelHelpers';
import {CustomFieldAttribute} from '../../../models/api/customFieldAttributeModel';
import {CustomFieldModel, CustomFieldsEnum} from '../../../models/api/customFieldModel';
import {InboxModel} from '../../../models/api/inboxModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {tryParsePublicId} from '../../../models/applications/publicIdTypes';
import {ResourceMap} from '../../../store/appTypes';
import {CustomAttributeInboxContainer} from '../../contacts/contactInformation/customAttributeInboxContainer';
import {CustomAttributeTeammateContainer} from '../../contacts/contactInformation/customAttributeTeammateContainer';
import {ContactCardChildItem} from './items/contactCardChildItem';
import {ContactCardLinkItem} from './items/contactCardLinkItem';

/*
 * Constants.
 */

// Handles the rendering of custom fields. Explicit so if a new custom field is added this will error.
const customFieldRenderOptions: Record<CustomFieldsEnum, RendererOf<string>> = {
  [CustomFieldsEnum.BOOLEAN]: (value) => {
    if (value === 'true') {
      return <Trans id="contact-card-custom-attribute-value-yes">Yes</Trans>;
    }
    return <Trans id="contact-card-custom-attribute-value-no">No</Trans>;
  },
  [CustomFieldsEnum.DATETIME]: (value) => (
    <SmartDateTimeFormatterContainer value={getValidTimestampFromValue(value)} />
  ),
  // Not used.
  [CustomFieldsEnum.EMAIL_DOMAIN]: (value) => value,
  [CustomFieldsEnum.INBOX]: (value) => (
    <CustomAttributeInboxContainer inboxPublicId={value} isCompact shouldAllowOverflow />
  ),
  [CustomFieldsEnum.NUMBER]: (value) => value,
  [CustomFieldsEnum.STRING]: (value) => value,
  [CustomFieldsEnum.ENUM]: (value) => value,
  [CustomFieldsEnum.TEAMMATE]: (value) => (
    <CustomAttributeTeammateContainer teammateEmail={value} isCompact shouldAllowOverflow />
  ),
  // Not used.
  [CustomFieldsEnum.UNKNOWN]: (value) => value,
};

// Handles the copying of custom fields. Explicit so if a new custom field is added this will error.
const customFieldCopyOptions: Record<CustomFieldsEnum, (value: string, optionalProp?: any) => string> = {
  [CustomFieldsEnum.BOOLEAN]: (value) => value,
  [CustomFieldsEnum.DATETIME]: (value) => DateTime.fromMillis(Number(value)).toISO(),
  // Not used.
  [CustomFieldsEnum.EMAIL_DOMAIN]: (value) => value,
  [CustomFieldsEnum.INBOX]: (value: string, inbox?: InboxModel) =>
    inbox ? `${inbox.name} (${value})` : value,
  [CustomFieldsEnum.NUMBER]: (value) => value,
  [CustomFieldsEnum.STRING]: (value) => value,
  [CustomFieldsEnum.ENUM]: (value) => value,
  [CustomFieldsEnum.TEAMMATE]: (value: string, teammate?: TeammateModel) =>
    teammate ? `${buildTeammateDisplayName(teammate)} (${value})` : value,
  // Not used.
  [CustomFieldsEnum.UNKNOWN]: (value) => value,
};

/*
 * Props.
 */

interface ContactCardDetailsCustomFieldsProps {
  customAttributes: ReadonlyArray<CustomFieldAttribute>;
  customFields: ResourceMap<CustomFieldModel>;
  teammates: ReadonlyArray<TeammateModel>;
  inboxes: ReadonlyArray<InboxModel>;
  availableItemsToRender: number;
}

interface RenderCustomFieldOptions {
  value: string;
  customField: CustomFieldModel;
}

/*
 * Component.
 */

export const ContactCardDetailsCustomFields: FC<ContactCardDetailsCustomFieldsProps> = (props) => {
  const renderedItems = useMemo(() => renderCustomFields(props), [props]);
  return <>{renderedItems}</>;
};

/*
 * Helpers.
 */

function renderCustomFields(props: ContactCardDetailsCustomFieldsProps) {
  const {customAttributes, customFields, teammates, inboxes, availableItemsToRender} = props;
  const customFieldsToRender = computeCustomFieldsToRender(customAttributes, customFields).slice(
    0,
    availableItemsToRender,
  );

  return _(customFieldsToRender)
    .map((options) => {
      const generateCopyFunc = customFieldCopyOptions[options.customField.type];
      switch (options.customField.type) {
        // Strings can potentially be links, this supports converting them to links.
        case CustomFieldsEnum.STRING:
          return (
            <ContactCardLinkItem
              key={options.customField.id}
              link={{url: options.value}}
              title={options.customField.name}
              shouldAllowMultiLineWrapping
            />
          );
        case CustomFieldsEnum.ENUM:
          return (
            <ContactCardChildItem
              key={options.customField.id}
              text={buildCustomFieldLabel(options.value, options.customField) ?? '-'}
              title={options.customField.name}
              shouldAllowMultiLineWrapping
            />
          );
        case CustomFieldsEnum.INBOX:
          return (
            <ContactCardChildItem
              key={options.customField.id}
              text={options.value}
              copyText={generateCopyFunc(
                options.value,
                inboxes.find((inbox) => inbox.id === tryParsePublicId(options.value, 'inbox')),
              )}
              title={options.customField.name}
              shouldAllowMultiLineWrapping
            >
              {customFieldRenderOptions[options.customField.type](options.value)}
            </ContactCardChildItem>
          );
        case CustomFieldsEnum.TEAMMATE:
          return (
            <ContactCardChildItem
              key={options.customField.id}
              text={options.value}
              copyText={generateCopyFunc(
                options.value,
                teammates.find((teammate) => teammate.email === options.value),
              )}
              title={options.customField.name}
              shouldAllowMultiLineWrapping
            >
              {customFieldRenderOptions[options.customField.type](options.value)}
            </ContactCardChildItem>
          );
        default:
          return (
            <ContactCardChildItem
              key={options.customField.id}
              text={options.value}
              copyText={generateCopyFunc(options.value)}
              title={options.customField.name}
              shouldAllowMultiLineWrapping
            >
              {customFieldRenderOptions[options.customField.type](options.value)}
            </ContactCardChildItem>
          );
      }
    })
    .compact()
    .value();
}

function computeCustomFieldsToRender(
  customAttributes: ReadonlyArray<CustomFieldAttribute>,
  customFields: ResourceMap<CustomFieldModel>,
): ReadonlyArray<RenderCustomFieldOptions> {
  return (
    _(customAttributes)
      .map((customAttribute) => {
        const customField = customFields[customAttribute.id];
        if (!customField) {
          return undefined;
        }

        return {
          value: customAttribute.value,
          customField,
        };
      })
      .compact()
      // Sort based on the custom field name.
      .sort((a, b) => compareName(a.customField.name, b.customField.name))
      .value()
  );
}
